// Hobbies Hub - Engaging section with interactive polls
// Main page showcasing hobbies with voting polls and community features

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, 
  Users, 
  TrendingUp, 
  Star, 
  Zap, 
  Target,
  Clock,
  BarChart3,
  Trophy,
  Calendar,
  Crown,
  Medal,
  Award
} from 'lucide-react';
import { useRealtime } from '../contexts/RealtimeContext';
import { listenToAllPolls } from '../firebase/polls';
import { listenToAllActivityRegistrations } from '../firebase/activityRegistrations';
import { listenToPublishedLeaderboards } from '../firebase/leaderboards';
import HobbiesPoll from '../components/HobbiesPoll';
import ActivityRegistration from '../components/ActivityRegistration';
import toast from 'react-hot-toast';

const HobbiesHub = () => {
  const { currentUser } = useRealtime();
  const [polls, setPolls] = useState([]);
  const [activePoll, setActivePoll] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activityRegistrations, setActivityRegistrations] = useState([]);
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(true);
  const [leaderboards, setLeaderboards] = useState([]);
  const [isLoadingLeaderboards, setIsLoadingLeaderboards] = useState(true);


  // Load polls
  useEffect(() => {
    const unsubscribe = listenToAllPolls((result) => {
      if (result.success) {
        setPolls(result.data);
        setIsLoading(false);
        
        // Set the first active poll as default
        const activePolls = result.data.filter(poll => poll.isActive && 
          (!poll.endTime || new Date(poll.endTime) > new Date()));
        if (activePolls.length > 0) {
          setActivePoll(activePolls[0]);
        }
      } else {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Load activity registrations
  useEffect(() => {
    const unsubscribe = listenToAllActivityRegistrations((result) => {
      if (result.success) {
        setActivityRegistrations(result.data);
        setIsLoadingRegistrations(false);
      } else {
        setIsLoadingRegistrations(false);
      }
    });

    return unsubscribe;
  }, []);

  // Load leaderboards
  useEffect(() => {
    const unsubscribe = listenToPublishedLeaderboards((result) => {
      if (result.success) {
        setLeaderboards(result.data);
        setIsLoadingLeaderboards(false);
      } else {
        setIsLoadingLeaderboards(false);
      }
    });

    return unsubscribe;
  }, []);

  // Get active polls
  const activePolls = polls.filter(poll => 
    poll.isActive && (!poll.endTime || new Date(poll.endTime) > new Date())
  );

  // Get completed polls
  const completedPolls = polls.filter(poll => 
    !poll.isActive || (poll.endTime && new Date(poll.endTime) <= new Date())
  );

  // Get active activity registrations
  const activeRegistrations = activityRegistrations.filter(registration => 
    registration.isActive && 
    (!registration.registrationEndTime || new Date(registration.registrationEndTime) > new Date())
  );


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading Hobbies Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center mb-6">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="mr-4"
              >
                <Heart className="h-12 w-12 text-purple-500" />
              </motion.div>
              <h1 className="text-5xl font-bold text-gray-900 dark:text-white">
                Hobbies Hub
              </h1>
            </div>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              Discover, share, and vote on your favorite hobbies! Join our community of passionate hobbyists 
              and help us find the most popular activities.
            </p>
            
            <div className="flex items-center justify-center space-x-8 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-purple-500" />
                <span>{polls.length} Polls</span>
              </div>
              <div className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-pink-500" />
                <span>{polls.reduce((sum, poll) => sum + (poll.totalVotes || 0), 0)} Total Votes</span>
              </div>
              <div className="flex items-center">
                <Star className="h-5 w-5 mr-2 text-yellow-500" />
                <span>{activePolls.length} Active Polls</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* All Activities Section - Unified Display */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
              <Zap className="h-8 w-8 mr-3 text-yellow-500" />
              Activities & Results
            </h2>
            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
              {activePolls.length > 0 && (
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{activePolls.length} polls</span>
                </div>
              )}
              {activeRegistrations.length > 0 && (
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-1" />
                  <span>{activeRegistrations.length} registrations</span>
                </div>
              )}
              {leaderboards.length > 0 && (
                <div className="flex items-center">
                  <Trophy className="h-4 w-4 mr-1" />
                  <span>{leaderboards.length} leaderboards</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {/* Active Polls */}
            {activePolls.map((poll, index) => (
              <motion.div
                key={`poll-${poll.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                  POLL
                </div>
                <HobbiesPoll pollId={poll.id} />
              </motion.div>
            ))}

            {/* Activity Registrations */}
            {activeRegistrations.map((registration, index) => (
              <motion.div
                key={`registration-${registration.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (activePolls.length + index) * 0.1 }}
                className="relative"
              >
                <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                  REGISTRATION
                </div>
                <ActivityRegistration registrationId={registration.id} />
              </motion.div>
            ))}

            {/* Completed Polls */}
            {completedPolls.slice(0, 3).map((poll, index) => (
              <motion.div
                key={`completed-poll-${poll.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (activePolls.length + activeRegistrations.length + index) * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {poll.title}
                    </h4>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      Completed
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {poll.description}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    {poll.options.slice(0, 3).map((option, optionIndex) => (
                      <div key={option.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 dark:text-gray-300">
                          {optionIndex + 1}. {option.text}
                        </span>
                      </div>
                    ))}
                    {poll.options.length > 3 && (
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        +{poll.options.length - 3} more options
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      <span>{poll.totalVotes || 0} votes</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{new Date(poll.createdAt?.toDate?.() || poll.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Leaderboards */}
            {leaderboards.slice(0, 3).map((leaderboard, index) => (
              <motion.div
                key={`leaderboard-${leaderboard.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (activePolls.length + activeRegistrations.length + completedPolls.length + index) * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-700 rounded-full mr-3 shadow-lg shadow-purple-500/25">
                        <Trophy className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                          {leaderboard.description || 'Leaderboard'}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {leaderboard.participants?.length || 0} participants competing
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-500 to-purple-700 text-white shadow-lg shadow-purple-500/25">
                      <Trophy className="h-3 w-3 mr-1" />
                      Leaderboard
                    </span>
                  </div>
                  
                  {leaderboard.participants && leaderboard.participants.length > 0 && (
                    <div className="mb-4">
                      <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                        <Trophy className="h-4 w-4 mr-2 text-yellow-500" />
                        Leaderboard
                      </h5>
                      
                      <div className="space-y-3 h-96 overflow-y-auto pr-2 pb-4" style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#d1d5db #f3f4f6'
                      }}>
                        {leaderboard.participants
                          .sort((a, b) => a.rank - b.rank)
                          .map((participant, participantIndex) => {
                            const getRankStyle = (rank) => {
                              if (rank === 1) {
                                return {
                                  container: 'bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 text-white shadow-lg shadow-purple-500/30 border-2 border-purple-400',
                                  icon: 'text-purple-100',
                                  rank: 'text-purple-100',
                                  name: 'text-white font-bold',
                                  achievement: 'text-purple-100'
                                };
                              }
                              if (rank === 2) {
                                return {
                                  container: 'bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30 border-2 border-blue-400',
                                  icon: 'text-blue-100',
                                  rank: 'text-blue-100',
                                  name: 'text-white font-bold',
                                  achievement: 'text-blue-100'
                                };
                              }
                              if (rank === 3) {
                                return {
                                  container: 'bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-700 text-white shadow-lg shadow-emerald-500/30 border-2 border-emerald-400',
                                  icon: 'text-emerald-100',
                                  rank: 'text-emerald-100',
                                  name: 'text-white font-bold',
                                  achievement: 'text-emerald-100'
                                };
                              }
                              return {
                                container: 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors',
                                icon: 'text-gray-600 dark:text-gray-400',
                                rank: 'text-gray-600 dark:text-gray-400',
                                name: 'text-gray-900 dark:text-white font-medium',
                                achievement: 'text-gray-600 dark:text-gray-400'
                              };
                            };

                            const getRankIcon = (rank) => {
                              if (rank === 1) return <Crown className="h-4 w-4" />;
                              if (rank === 2) return <Medal className="h-4 w-4" />;
                              if (rank === 3) return <Award className="h-4 w-4" />;
                              return <span className="font-bold text-sm">{rank}</span>;
                            };

                            const getRankBadge = (rank) => {
                              if (rank === 1) return '👑';
                              if (rank === 2) return '💎';
                              if (rank === 3) return '⭐';
                              return `#${rank}`;
                            };

                            const style = getRankStyle(participant.rank);

                            return (
                              <motion.div
                                key={participantIndex}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: participantIndex * 0.1 }}
                                className={`flex items-center justify-between p-3 rounded-xl ${style.container} transform hover:scale-105 transition-all duration-200`}
                              >
                                <div className="flex items-center">
                                  <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${style.icon} bg-white/20 backdrop-blur-sm`}>
                                    {getRankIcon(participant.rank)}
                                  </div>
                                  <div className="flex items-center">
                                    <span className="text-lg mr-2">{getRankBadge(participant.rank)}</span>
                                    <div>
                                      <p className={`font-semibold text-sm ${style.name}`}>{participant.name}</p>
                                      {participant.achievement && (
                                        <p className={`text-xs ${style.achievement}`}>{participant.achievement}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="text-right">
                                  <div className={`px-2 py-1 rounded-full text-xs font-bold ${style.rank} bg-white/20 backdrop-blur-sm`}>
                                    Rank #{participant.rank}
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      <span>{leaderboard.participants?.length || 0} participants</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{new Date(leaderboard.createdAt?.toDate?.() || leaderboard.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* No Content Message */}
        {polls.length === 0 && activeRegistrations.length === 0 && leaderboards.length === 0 && (
          <div className="text-center py-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No activities found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                No polls or activity registrations have been created yet. Check back later!
              </p>
              {currentUser && (
                <button
                  onClick={() => toast.success('Admin panel access required to create activities')}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Create Activity
                </button>
              )}
            </motion.div>
          </div>
        )}

        {/* Community Stats */}
        <div className="mt-16 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Community Statistics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {polls.length}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">Total Polls</p>
            </div>
            
            <div className="text-center">
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {polls.reduce((sum, poll) => sum + (poll.totalVotes || 0), 0)}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">Total Votes</p>
            </div>
            
            <div className="text-center">
              <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {activePolls.length}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">Active Polls</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HobbiesHub;
